'use client';

import { Fragment, memo, useMemo } from 'react';

import { Spinner } from '@air/primitive-spinner';
import { getProviderNameFromSSOOptions } from '@air/utils-auth';

import { SAMLProvidersSelectListItem } from '~/components/auth/SAMLProvidersSelectListItem';
import { useAccountSSOOptions } from '~/hooks/api/useAccountSSOOptions';

export type SAMLProvidersSelectListProps = {
  email: string;
};

export const SAMLProvidersSelectList = memo(({ email }: SAMLProvidersSelectListProps) => {
  const { data: accountSSOOptions, isError, isLoading } = useAccountSSOOptions({ email });

  const filteredOptions = useMemo(
    () => accountSSOOptions?.filter((option) => getProviderNameFromSSOOptions(option)),
    [accountSSOOptions],
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-6">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="w-full py-6 text-center text-14 text-neutral-10">Unable to load SSO providers. Please try again.</p>
    );
  }

  if (filteredOptions?.length === 0) {
    return (
      <p className="w-full py-6 text-center text-14 text-neutral-10">No SSO providers available for this account.</p>
    );
  }

  return (
    <div className="flex w-full flex-col">
      {filteredOptions?.map((option, index) => {
        const provider = getProviderNameFromSSOOptions(option);

        if (!provider) return null;

        return (
          <Fragment key={option.name}>
            {index > 0 && <div className="mx-2 h-px bg-neutral-6" />}
            <SAMLProvidersSelectListItem image={option.image} name={option.name} provider={provider} />
          </Fragment>
        );
      })}
    </div>
  );
});

SAMLProvidersSelectList.displayName = 'SAMLProvidersSelectList';
